import { GetStaticPropsContext, GetStaticPropsResult, NextPage } from "next";
import { createContext, useContext } from "react";

import PageHead, { TwitterCard } from "~/components/common/PageHead";
import PcRaidDetail from "~/components/raid/page/[id]/PcRaidDetail";
import SpRaidDetail from "~/components/raid/page/[id]/SpRaidDetail";
import { Props } from "~/components/raid/page/[id]/lib";
import useIsSpMode from "~/lib/client/hooks/useIsSpMode";
import { Layout } from "~/lib/client/layout";
import { dayjs } from "~/lib/date-util";
import { createGraphqlClient } from "~/lib/graphql/graphqlClient";
import { convertMarkdownToHtml } from "~/lib/markdown-helper";
import "~/lib/monkeypatch";

export async function getStaticPaths() {
  const client = createGraphqlClient();
  const { raids } = await client.raidIndexContentForSsg();
  const paths = raids.map((raid) => ({
    params: { id: `${raid.id.toString()}` },
  }));
  return { paths, fallback: "blocking" };
}

export async function getStaticProps(
  context: GetStaticPropsContext
): Promise<GetStaticPropsResult<Props>> {
  const id = context.params?.id;
  if (typeof id !== "string") {
    return {
      notFound: true,
    };
  }

  if (Number(id) == 0) {
    return {
      notFound: true,
    };
  }

  const graphqlClient = createGraphqlClient();
  const { raid } = await graphqlClient.raidDetailContent({ id: Number(id) });

  return {
    props: {
      raid: {
        ...raid,
        description: await convertMarkdownToHtml(raid.description),
      },
      generatedAt: dayjs().tz("Asia/Tokyo").format("YYYY/MM/DD HH:mm"),
    },
    revalidate: 60 * 60,
  };
}

const RaidDetailContext = createContext({} as Props);
export const useRaidDetailContext = () => {
  return useContext(RaidDetailContext);
};

const Page: NextPage<Props> = ({ raid, generatedAt }) => {
  const isSpMode = useIsSpMode();
  const Page = isSpMode ? SpRaidDetail : PcRaidDetail;

  const twitterCard: TwitterCard = {
    cardType: "summary_large_image",
    creator: "FUSSY",
    ogImage: (() => {
      if (raid.id == 1) {
        return "https://d1stpor4o81wuq.cloudfront.net/images/レイド さらばBAR.png";
      }

      if (raid.id == 2) {
        return "https://d1stpor4o81wuq.cloudfront.net/images/%E3%83%AC%E3%82%A4%E3%83%88%E3%82%99%20%E8%A3%8F%E3%81%95%E3%82%89%E3%81%AF%E3%82%99%EF%BC%88%E8%89%B2%E5%A4%89%E6%9B%B4%EF%BC%89OGP.png";
      }

      return raid.bannerImageUrl;
    })(),
  };
  const Head = () => (
    <PageHead
      title={raid.title}
      ogDescription={raid.description}
      ogUrl={`/raid/${raid.id}`}
      twitterCard={twitterCard}
    />
  );

  if (typeof isSpMode === "undefined") {
    return <Head />;
  }

  return (
    <RaidDetailContext.Provider
      value={{
        raid,
        generatedAt,
      }}
    >
      <Head />
      <Layout layoutType={"YTLayout"}>
        <Page />
      </Layout>
    </RaidDetailContext.Provider>
  );
};

export default Page;
